import React, { useEffect, useRef, useState } from 'react';
import Appear from '../components/ui/Appear';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import BackLink from '../components/BackLink';
import { SecondaryAction } from '../components/PageHeader';
import { Button, IconButton } from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import LogoSpinner from '../components/ui/LogoSpinner';
import Chip from '../components/ui/Chip';
import { Field, TextArea } from '../components/ui/Field';
import SegTrack from '../components/ui/SegTrack';
import Tooltip from '../components/ui/Tooltip';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import { errorMessage, getChatStatus, updatePlan } from './api';
import { useNodePlan } from './useNodePlan';
import { usePlanChatStream } from './usePlanChatStream';
import NoAccessPage from './NoAccessPage';
import PresenceRow from './PresenceRow';
import SharePanel from './SharePanel';
import ExportModal from './ExportModal';
import PlanChat from './PlanChat';
import MostLikedPanel from './MostLikedPanel';
import DocumentView from './DocumentView';
import Canvas from './canvas/Canvas';
import { PROPOSED_DOM_ID } from './DocContents';
import { PILL, ROW_BUTTON, TONAL_SMALL, sectionDomId, useReturnFocus } from './DocSection';
import PageLayout from '../components/ui/PageLayout';
import type { PlanPatch, PlanResponse, PresenceUser } from './types';

const PAGE = 'pt-4 lg:pt-6';
const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';
const MOTION = 'transition-colors duration-150 ease-emphasized motion-reduce:transition-none';

type PlanView = 'document' | 'canvas';

const VIEW_OPTIONS = [
  { key: 'document', label: 'Document' },
  { key: 'canvas', label: 'Canvas' },
];

const viewKey = (planId: string) => `node-plan:view:${planId}`;

/** The view this browser last used for this plan. Document when unset or storage is unavailable. */
const readView = (planId: string): PlanView => {
  try {
    return window.localStorage.getItem(viewKey(planId)) === 'canvas' ? 'canvas' : 'document';
  } catch {
    return 'document';
  }
};

const writeView = (planId: string, view: PlanView) => {
  try {
    window.localStorage.setItem(viewKey(planId), view);
  } catch {
    /* private mode or full storage: the choice lasts until reload */
  }
};

interface InlineTextProps {
  value: string;
  canEdit: boolean;
  label: string;
  multiline?: boolean;
  /** One line: the editor sits in a row with its buttons, for the canvas HUD strip. */
  compact?: boolean;
  required?: boolean;
  maxLength?: number;
  placeholder?: string;
  inputClassName?: string;
  onSave: (value: string) => Promise<boolean>;
  /** The display, as phrasing content: it goes inside a button when editable. */
  children: React.ReactNode;
  /** The block element around the display (h1, p). Not applied while editing. */
  wrap?: (display: React.ReactNode) => React.ReactNode;
}

const asIs = (n: React.ReactNode) => n;

/** Click to edit. Enter saves (Shift+Enter is a new line when multiline), Escape cancels. */
const InlineText: React.FC<InlineTextProps> = ({
  value,
  canEdit,
  label,
  multiline,
  required,
  maxLength,
  placeholder,
  inputClassName = '',
  onSave,
  children,
  wrap = asIs,
  compact = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const trigger = useReturnFocus<HTMLButtonElement>(editing);

  useEffect(() => {
    if (editing) {
      ref.current?.focus();
      ref.current?.select();
    }
  }, [editing]);

  useEffect(() => {
    if (!canEdit) setEditing(false);
  }, [canEdit]);

  if (!canEdit) return <>{wrap(children)}</>;

  if (!editing) {
    const open = () => {
      setDraft(value);
      setEditing(true);
    };
    // The text stays outside the button so a heading keeps its own name; the
    // pencil is the keyboard path, a click on the text is the pointer shortcut.
    return (
      <div className={`group/inline flex gap-1 ${compact ? 'min-w-0 items-center' : 'items-start'}`}>
        <div className="min-w-0 cursor-text" onClick={open}>
          {wrap(children)}
        </div>
        <Tooltip label={`Edit ${label.toLowerCase()}`}>
          <IconButton
            ref={trigger}
            onClick={open}
            label={`Edit ${label.toLowerCase()}`}
            className="opacity-60 hover:opacity-100 group-hover/inline:opacity-100 focus-visible:opacity-100"
          >
            <PencilIcon />
          </IconButton>
        </Tooltip>
      </div>
    );
  }

  const save = async () => {
    const next = required ? draft.trim() : draft;
    if (busy || (required && !next)) return;
    if (next === value) {
      setEditing(false);
      return;
    }
    setBusy(true);
    const ok = await onSave(next);
    setBusy(false);
    if (ok) setEditing(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setEditing(false);
    } else if (e.key === 'Enter' && !(multiline && e.shiftKey)) {
      e.preventDefault();
      save();
    }
  };

  const shared = {
    ref,
    value: draft,
    disabled: busy,
    maxLength,
    placeholder,
    'aria-label': label,
    onKeyDown,
    className: inputClassName,
  };

  return (
    <div className={compact ? 'flex min-w-0 items-center gap-1' : 'space-y-2'}>
      {multiline ? (
        <TextArea {...shared} rows={2} onChange={(e) => setDraft(e.target.value)} />
      ) : (
        <Field {...shared} onChange={(e) => setDraft(e.target.value)} />
      )}
      <div className="flex items-center gap-1">
        <button type="button" onClick={save} disabled={busy || (required && !draft.trim())} className={TONAL_SMALL}>
          Save
        </button>
        <button type="button" onClick={() => setEditing(false)} disabled={busy} className={ROW_BUTTON}>
          Cancel
        </button>
      </div>
    </div>
  );
};

const PencilIcon: React.FC = () => <FilledIcon d={PENCIL} />;

type SheetKind = 'share' | 'liked' | 'chat';

const SHEET_TITLE: Record<SheetKind, string> = {
  share: 'Share',
  liked: 'Most liked',
  chat: 'Ask about this plan',
};

interface PlanHeaderProps {
  data: PlanResponse;
  canManage: boolean;
  view: PlanView;
  onView: (view: PlanView) => void;
  onPatch: (patch: PlanPatch, success?: string) => Promise<boolean>;
  presence: PresenceUser[];
  chatAvailable: boolean;
  sheet: SheetKind | null;
  onSheet: (kind: SheetKind) => void;
  onExport: () => void;
  showDone: boolean;
  doneCount: number;
  onShowDone: (on: boolean) => void;
}

// Filled paths on a fill-less root, one per header key.
const SHARE_ICON =
  'M15 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4zM6 10V7H4v3H1v2h3v3h2v-3h3v-2z';
const LIKED_ICON =
  'M12 21.4 10.6 20C5.4 15.4 2 12.3 2 8.5 2 5.4 4.4 3 7.5 3c1.7 0 3.4.8 4.5 2.1C13.1 3.8 14.8 3 16.5 3 19.6 3 22 5.4 22 8.5c0 3.8-3.4 6.9-8.6 11.5z';
const EXPORT_ICON = 'M5 20h14v-2H5zM19 9h-4V3H9v6H5l7 7z';
const CHAT_ICON = 'M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2zm3 5v2h10V9zm0 4v2h7v-2z';
const DONE_ICON = 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-2 15-5-5 1.4-1.4 3.6 3.6 7.6-7.6L19 8z';

const SHEET_ICON: Record<SheetKind, string> = { share: SHARE_ICON, liked: LIKED_ICON, chat: CHAT_ICON };

const TOOLTIP: Record<SheetKind | 'export', string> = {
  share: 'See who has this plan and add people',
  liked: 'The notes with the most votes, across every topic',
  export: 'The whole plan as one markdown file',
  chat: 'Ask questions about the plan as it stands',
};

/** Share, Most liked, Export and Ask: the same four keys in both header modes. */
const HeaderKeys: React.FC<{
  compact: boolean;
  chatAvailable: boolean;
  sheet: SheetKind | null;
  onSheet: (kind: SheetKind) => void;
  onExport: () => void;
}> = ({ compact, chatAvailable, sheet, onSheet, onExport }) => {
  const keys: { kind: SheetKind | 'export'; label: string; narrow: boolean }[] = [
    { kind: 'share', label: 'Share', narrow: true },
    { kind: 'liked', label: 'Most liked', narrow: false },
    { kind: 'export', label: 'Export', narrow: false },
    ...(chatAvailable ? [{ kind: 'chat' as const, label: 'Ask about this plan', narrow: true }] : []),
  ];
  return (
    <>
      {keys.map(({ kind, label, narrow }) => {
        const isSheet = kind !== 'export';
        const open = isSheet && sheet === kind;
        const onClick = () => (kind === 'export' ? onExport() : onSheet(kind));
        const d = kind === 'export' ? EXPORT_ICON : SHEET_ICON[kind];
        if (compact) {
          // Under 760px the canvas strip keeps Share and Ask; the document view has all four.
          return (
            <Tooltip key={kind} label={TOOLTIP[kind]}>
              <button
                type="button"
                aria-label={label}
                aria-expanded={isSheet ? open : undefined}
                aria-haspopup="dialog"
                onClick={onClick}
                className={`${HUD_KEY} ${narrow ? '' : 'max-[759px]:hidden'} ${open ? '!bg-primary/15 !text-primary-ink' : ''}`}
              >
                <FilledIcon d={d} />
              </button>
            </Tooltip>
          );
        }
        return (
          <Tooltip key={kind} label={TOOLTIP[kind]}>
            <button
              type="button"
              aria-expanded={isSheet ? open : undefined}
              aria-haspopup="dialog"
              onClick={onClick}
              className={`np-state inline-flex min-h-[36px] items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-sm font-medium ${
                open ? 'bg-primary/15 text-primary-ink' : 'text-on-surface-variant hover:text-on-surface'
              } ${MOTION} ${FOCUS}`}
            >
              <FilledIcon d={d} />
              {label}
            </button>
          </Tooltip>
        );
      })}
    </>
  );
};

const PlanHeader: React.FC<PlanHeaderProps> = ({
  data,
  canManage,
  view,
  onView,
  onPatch,
  presence,
  chatAvailable,
  sheet,
  onSheet,
  onExport,
  showDone,
  doneCount,
  onShowDone,
}) => {
  const { plan, members } = data;
  const locked = !!plan.locked_at;
  const archived = !!plan.archived_at;
  const canEditPlan = canManage && !archived;
  const owner = `${plan.owner.firstname || ''} ${plan.owner.lastname || ''}`.trim();
  const others = members.filter((m) => m.role !== 'owner').length;

  return (
    <header>
      <BackLink to="/node-plans" label="Node Plans" />
      <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <InlineText
                value={plan.title}
                canEdit={canEditPlan}
                label="Plan title"
                required
                maxLength={200}
                inputClassName="text-xl font-bold"
                onSave={(title) => onPatch({ title })}
                wrap={(display) => (
                  <h1 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight text-on-surface text-balance break-words">
                    {display}
                  </h1>
                )}
              >
                {plan.title}
              </InlineText>
            </div>
            <Appear show={presence.length > 0} as="div" className="flex-shrink-0 pt-1.5">
              <PresenceRow presence={presence} />
            </Appear>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Appear show={!!locked} className={PILL}>
              <FilledIcon d={LOCK_CLOSED} className="h-3.5 w-3.5" />
              Locked
            </Appear>
            <Appear show={!!archived} className={PILL}>Archived</Appear>
            <span className="text-xs text-on-surface-variant">
              {owner ? `Owned by ${owner}` : 'Owner unknown'}
              {others > 0 ? `, shared with ${others}` : ''}
            </span>
            <span className="text-xs tabular-nums text-on-surface-variant">rev {plan.version}</span>
          </div>
          <div className="mt-2 max-w-2xl">
            <InlineText
              value={plan.description}
              canEdit={canEditPlan}
              label="Plan description"
              multiline
              placeholder="What this plan is for"
              onSave={(description) => onPatch({ description })}
              wrap={(display) =>
                plan.description || canEditPlan ? (
                  <p className="whitespace-pre-wrap break-words text-sm text-on-surface-variant">{display}</p>
                ) : null
              }
            >
              {plan.description || <span className="italic">Add a description</span>}
            </InlineText>
          </div>
        </div>
        <div className="flex flex-shrink-0 flex-col gap-2 sm:items-end sm:pt-1">
          <div className="flex flex-wrap items-center gap-2">
            <SegTrack label="Plan view" options={VIEW_OPTIONS} value={view} onChange={(k) => onView(k as PlanView)} />
            <Appear show={doneCount > 0 || showDone}>
              <Tooltip label={showDone ? 'Hide done topics' : 'Show done topics'}>
                <Chip pressed={showDone} onClick={() => onShowDone(!showDone)} leading={<FilledIcon d={DONE_ICON} className="h-3.5 w-3.5" />}>
                  {showDone ? (
                    'Hide done'
                  ) : (
                    <>
                      Show done <span className="tabular-nums">({doneCount})</span>
                    </>
                  )}
                </Chip>
              </Tooltip>
            </Appear>
            {canEditPlan && (
              <SecondaryAction
                onClick={() => onPatch({ locked: !locked }, locked ? 'Plan unlocked' : 'Plan locked')}
                label={locked ? 'Unlock this plan' : 'Lock this plan'}
              >
                <FilledIcon d={locked ? LOCK_CLOSED : LOCK_OPEN} className="h-3.5 w-3.5" />
                {locked ? 'Unlock' : 'Lock'}
              </SecondaryAction>
            )}
          </div>
          <div className="-ml-3 flex flex-wrap items-center gap-0.5 sm:ml-0 sm:-mr-3">
            <HeaderKeys compact={false} chatAvailable={chatAvailable} sheet={sheet} onSheet={onSheet} onExport={onExport} />
          </div>
        </div>
      </div>
    </header>
  );
};

// ── The canvas HUD: the header collapsed to one strip so the canvas gets the height ──

const HUD_KEY =
  'inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-surface-container-high text-on-surface-variant shadow-[inset_0_1px_0_rgba(255,255,255,0.04),inset_0_-1px_0_rgba(0,0,0,0.22)] np-state hover:text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors duration-150 ease-emphasized motion-reduce:transition-none';

/** Filled paths on a fill-less root, the house icon construction. */
const FilledIcon: React.FC<{ d: string; className?: string }> = ({ d, className = 'h-4 w-4' }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="none">
    <path fill="currentColor" d={d} />
  </svg>
);

const LOCK_CLOSED =
  'M12 2a5 5 0 0 0-5 5v3H6a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V11a1 1 0 0 0-1-1h-1V7a5 5 0 0 0-5-5zm-3 8V7a3 3 0 1 1 6 0v3H9z';
const PENCIL = 'M4 17.2V20h2.8l9.9-9.9-2.8-2.8zM18.4 8.4l-2.8-2.8 1.5-1.5a1 1 0 0 1 1.4 0l1.4 1.4a1 1 0 0 1 0 1.4z';
const LOCK_OPEN = 'M12 2a5 5 0 0 0-5 5h2a3 3 0 1 1 6 0v3H6a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V11a1 1 0 0 0-1-1h-1V7a5 5 0 0 0-5-5z';

const PlanHud: React.FC<PlanHeaderProps> = ({
  data,
  canManage,
  view,
  onView,
  onPatch,
  presence,
  chatAvailable,
  sheet,
  onSheet,
  onExport,
  showDone,
  doneCount,
  onShowDone,
}) => {
  const { plan } = data;
  const locked = !!plan.locked_at;
  const archived = !!plan.archived_at;
  const canEditPlan = canManage && !archived;
  const lockLabel = locked ? 'Unlock this plan' : 'Lock this plan';

  return (
    <header className="flex h-12 items-center gap-2 border-b border-outline-variant bg-surface-container-low px-2 sm:px-3">
      <BackLink to="/node-plans" label="Node Plans" className="flex-shrink-0" />
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="min-w-0 flex-shrink">
          <InlineText
            value={plan.title}
            canEdit={canEditPlan}
            label="Plan title"
            required
            maxLength={200}
            compact
            inputClassName="min-w-0 flex-1 !py-1 font-semibold"
            onSave={(title) => onPatch({ title })}
            wrap={(display) => (
              <h1 title={plan.title} className="truncate text-[15px] font-semibold leading-tight text-on-surface">
                {display}
              </h1>
            )}
          >
            {plan.title}
          </InlineText>
        </div>
        <span className="hidden flex-shrink-0 whitespace-nowrap text-xs tabular-nums text-on-surface-variant min-[760px]:inline">
          rev {plan.version}
        </span>
        <Appear show={!!locked} className={`${PILL} hidden flex-shrink-0 min-[760px]:inline-flex`}>
          <FilledIcon d={LOCK_CLOSED} className="h-3 w-3" />
          Locked
        </Appear>
        <Appear show={!!archived} className={`${PILL} hidden flex-shrink-0 min-[760px]:inline-flex`}>Archived</Appear>
        <Appear show={presence.length > 0} as="div" className="hidden flex-shrink-0 min-[760px]:flex">
          <PresenceRow presence={presence} />
        </Appear>
      </div>
      <HeaderKeys compact chatAvailable={chatAvailable} sheet={sheet} onSheet={onSheet} onExport={onExport} />
      <Appear show={doneCount > 0 || showDone} className="flex-shrink-0">
        <Tooltip label={showDone ? 'Hide done topics' : 'Show done topics'}>
          <button
            type="button"
            aria-label={showDone ? 'Hide done' : `Show done (${doneCount})`}
            aria-pressed={showDone}
            onClick={() => onShowDone(!showDone)}
            className={`${HUD_KEY} ${showDone ? '!bg-primary/15 !text-primary-ink' : ''}`}
          >
            <FilledIcon d={DONE_ICON} />
          </button>
        </Tooltip>
      </Appear>
      <SegTrack label="Plan view" options={VIEW_OPTIONS} value={view} onChange={(k) => onView(k as PlanView)} className="flex-shrink-0" />
      {canEditPlan && (
        // Under 760px Lock lives in the canvas's empty-space menu instead.
        <Tooltip label={lockLabel}>
          <button
            type="button"
            aria-label={lockLabel}
            aria-pressed={locked}
            onClick={() => onPatch({ locked: !locked }, locked ? 'Plan unlocked' : 'Plan locked')}
            className={`${HUD_KEY} max-[759px]:hidden`}
          >
            <FilledIcon d={locked ? LOCK_CLOSED : LOCK_OPEN} />
          </button>
        </Tooltip>
      )}
    </header>
  );
};

const LoadingState: React.FC = () => (
  <PageLayout width="full" kind="record" className={PAGE}>
    <LogoSpinner label="Loading plan" />
  </PageLayout>
);

const MessageState: React.FC<{ title: string; body: string; action?: { label: string; onClick: () => void } }> = ({
  title,
  body,
  action,
}) => (
  <PageLayout width="full" kind="workspace" className={PAGE}>
    <BackLink to="/node-plans" label="Node Plans" />
    <EmptyState
      className="mx-auto mt-10 max-w-md"
      title={title}
      titleAs="h1"
      action={action && <Button variant="tonal" onClick={action.onClick}>{action.label}</Button>}
    >
      {body}
    </EmptyState>
  </PageLayout>
);

/**
 * A right-hand sheet over the page: the whole screen under 760px, a floating panel
 * above it. Not modal: the plan stays readable and scrollable beside it. Escape closes
 * it and focus goes back to the key that opened it.
 */
const PlanSheet: React.FC<{ kind: SheetKind; onClose: () => void; children: React.ReactNode; scroll?: boolean }> = ({
  kind,
  onClose,
  children,
  scroll = true,
}) => {
  const heading = useRef<HTMLHeadingElement>(null);
  const panel = useRef<HTMLElement>(null);
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    opener.current = document.activeElement as HTMLElement | null;
    return () => {
      // Only when focus was left inside the sheet: a click elsewhere keeps its own focus.
      const active = document.activeElement;
      if (!active || active === document.body) opener.current?.focus?.();
    };
  }, []);

  // A panel that focuses its own field on mount (the chat input) keeps it.
  useEffect(() => {
    if (!panel.current?.contains(document.activeElement)) heading.current?.focus();
  }, [kind]);

  const id = `np-sheet-${kind}`;
  return ReactDOM.createPortal(
    <aside
      ref={panel}
      role="dialog"
      aria-labelledby={id}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !e.defaultPrevented) {
          e.stopPropagation();
          onClose();
        }
      }}
      className="nokey fixed inset-0 z-50 flex flex-col bg-surface-container p-4 shadow-[var(--np-overlay-shadow)] min-[760px]:inset-auto min-[760px]:bottom-3 min-[760px]:right-3 min-[760px]:top-3 min-[760px]:w-[380px] min-[760px]:rounded-xl"
    >
      <div className="flex items-center gap-2 pb-3">
        <h2 id={id} ref={heading} tabIndex={-1} className="min-w-0 flex-1 truncate text-[17px] font-bold text-on-surface focus:outline-none">
          {SHEET_TITLE[kind]}
        </h2>
        <Tooltip label={`Close ${SHEET_TITLE[kind].toLowerCase()}`}>
          <IconButton
            label={`Close ${SHEET_TITLE[kind].toLowerCase()}`}
            onClick={onClose}
            className="-mr-1"
          >
            <FilledIcon d="M6.4 5 12 10.6 17.6 5 19 6.4 13.4 12 19 17.6 17.6 19 12 13.4 6.4 19 5 17.6 10.6 12 5 6.4z" />
          </IconButton>
        </Tooltip>
      </div>
      <div className={`flex min-h-0 flex-1 flex-col ${scroll ? 'overflow-y-auto' : ''}`}>{children}</div>
    </aside>,
    document.body,
  );
};

const NARROW = '(max-width: 759px)';

const NodePlanPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  // Keyed by plan so another plan opens with done topics hidden, without a fetch that shows
  // them first. Coming back to the same plan finds the switch where it was left.
  const [doneShown, setDoneShown] = useState<string | null>(null);
  const showDone = doneShown === id;
  const { data, canManage, loading, error, reload, presence, liveVersion, doneCount } = useNodePlan(id, { showDone });
  const logout = useUserStore((s: any) => s.logout);
  const userId: string | null = useUserStore((s: any) => s.user?.id ?? null);
  const chat = usePlanChatStream(id);
  const [sheet, setSheet] = useState<SheetKind | null>(null);
  const [exporting, setExporting] = useState(false);
  const [chatAvailable, setChatAvailable] = useState(false);
  const role = data?.role ?? null;
  const [view, setViewState] = useState<PlanView>(() => readView(id));
  // "Open in document" from the canvas: the section to show once the document has rendered.
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);

  useEffect(() => {
    setViewState(readView(id));
    setSheet(null);
    setExporting(false);
  }, [id]);

  // Hidden unless the server says the assistant is set up and this role may ask.
  useEffect(() => {
    if (!role) return undefined;
    let live = true;
    getChatStatus(id)
      .then((r) => live && setChatAvailable(!!r?.available))
      .catch(() => live && setChatAvailable(false));
    return () => {
      live = false;
    };
  }, [id, role]);

  useEffect(() => {
    if (!chatAvailable && sheet === 'chat') setSheet(null);
  }, [chatAvailable, sheet]);

  useEffect(() => {
    if (view !== 'document' || !scrollTarget) return;
    const el = document.getElementById(scrollTarget);
    setScrollTarget(null);
    if (!el) return;
    const reduce = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView?.({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    el.focus({ preventScroll: true });
  }, [view, scrollTarget]);

  const setView = (next: PlanView) => {
    setViewState(next);
    writeView(id, next);
  };

  if (error === 'not_a_member') return <NoAccessPage planId={id} />;
  if (!data) {
    if (loading) return <LoadingState />;
    if (error === 'not_found') {
      return <MessageState title="This plan does not exist." body="It may have been removed, or the link is wrong." />;
    }
    if (error === 'signed_out') {
      return (
        <MessageState
          title="Your session has ended."
          body="Sign in again to open this plan."
          action={{ label: 'Sign in again', onClick: logout }}
        />
      );
    }
    return (
      <MessageState
        title="Could not load this plan."
        body="Check your connection and try again."
        action={{ label: 'Try again', onClick: reload }}
      />
    );
  }

  const onPatch = async (patch: PlanPatch, success?: string): Promise<boolean> => {
    try {
      await updatePlan(id, patch);
      if (success) toast.success(success);
      await reload();
      return true;
    } catch (e) {
      toast.error(errorMessage(e, 'Could not save the plan.'));
      reload();
      return false;
    }
  };

  const writable = !data.plan.locked_at && !data.plan.archived_at;

  const showInDocument = (nodeId: string) => {
    const node = data.nodes.find((n) => n.id === nodeId);
    if (node) setScrollTarget(node.status === 'proposed' ? PROPOSED_DOM_ID : sectionDomId(node.id));
    setView('document');
  };

  const headerProps: PlanHeaderProps = {
    data,
    canManage,
    view,
    onView: setView,
    onPatch,
    presence,
    chatAvailable,
    sheet,
    onSheet: (kind) => setSheet((cur) => (cur === kind ? null : kind)),
    onExport: () => setExporting(true),
    showDone,
    doneCount,
    onShowDone: (on) => setDoneShown(on ? id : null),
  };

  const overlays = (
    <>
      {sheet === 'share' && (
        <PlanSheet kind="share" onClose={() => setSheet(null)}>
          <SharePanel planId={id} data={data} userId={userId} onPatch={onPatch} reload={reload} />
        </PlanSheet>
      )}
      {sheet === 'liked' && (
        <PlanSheet kind="liked" onClose={() => setSheet(null)}>
          <MostLikedPanel
            planId={id}
            tree={data.tree}
            version={data.plan.version}
            onOpen={(nodeId) => {
              // Full screen on a phone: the sheet steps aside so the section can be seen.
              if (window.matchMedia?.(NARROW).matches) setSheet(null);
              showInDocument(nodeId);
            }}
          />
        </PlanSheet>
      )}
      {sheet === 'chat' && chatAvailable && (
        <PlanSheet kind="chat" onClose={() => setSheet(null)} scroll={false}>
          <PlanChat chat={chat} planVersion={Math.max(data.plan.version, liveVersion ?? 0)} />
        </PlanSheet>
      )}
      {exporting && <ExportModal planId={id} title={data.plan.title} onClose={() => setExporting(false)} />}
    </>
  );

  if (view === 'document') {
    return (
      <PageLayout width="full" kind="record" className={PAGE}>
        <PlanHeader {...headerProps} />
        <DocumentView
          planId={id}
          data={data}
          canManage={canManage}
          writable={writable}
          reload={reload}
          showDone={showDone}
          onShowDone={headerProps.onShowDone}
        />
        {overlays}
      </PageLayout>
    );
  }

  // The canvas leaves the reading column: a one-line HUD, then the canvas edge to edge
  // down to the bottom of the viewport. The app shell's <main> adds no column of its own.
  const canEditPlan = canManage && !data.plan.archived_at;
  return (
    <>
      <PlanHud {...headerProps} />
      <Canvas
        planId={id}
        data={data}
        canManage={canManage}
        writable={writable}
        reload={reload}
        onOpenInDocument={(nodeId) => (nodeId ? showInDocument(nodeId) : setView('document'))}
        onToggleLock={
          canEditPlan
            ? () => onPatch({ locked: !data.plan.locked_at }, data.plan.locked_at ? 'Plan unlocked' : 'Plan locked')
            : undefined
        }
        showDone={showDone}
        onShowDone={headerProps.onShowDone}
      />
      {overlays}
    </>
  );
};

export default NodePlanPage;
